import type { ReactNode } from "react";

// MetaChip — informational (NON-status) white hairline pill: an optional leading
// stroke icon + a sentence-case label. Use for factual metadata a couple/therapist
// glances at — location, marriage length, kids, "Group", a "Confidential" lock,
// coach attribution. Distinct from `Tag` and the status `Chip` (both uppercase /
// status-adjacent): MetaChip is quiet, sentence-case, and carries no role color.
// The consumer supplies the icon (an <Icon/>, an inline SVG, etc.); it renders at
// a faint tint and is treated as decorative — the label carries the meaning.

export interface MetaChipProps {
    /** Optional leading stroke icon. Rendered decoratively at a faint tint; the
     *  label carries the accessible meaning. */
    icon?: ReactNode;
    /** Sentence-case label (e.g. "Austin, TX", "Married 6 years", "Group",
     *  "Confidential"). NOT uppercased — MetaChip is informational, not a marker. */
    children: ReactNode;
    className?: string;
}

export function MetaChip({ icon, children, className }: MetaChipProps) {
    return (
        <span
            className={[
                "inline-flex items-center gap-2 rounded-[var(--radius-pill)] border border-[var(--border)]",
                "bg-[var(--surface)] px-2 py-1",
                "[font:var(--type-caption)] text-[var(--text-secondary)]",
                className,
            ]
                .filter(Boolean)
                .join(" ")}
        >
            {icon && (
                <span aria-hidden="true" className="inline-flex shrink-0 text-[var(--text-tertiary)]">
                    {icon}
                </span>
            )}
            {children}
        </span>
    );
}
